import { flushSync } from "react-dom";
import { coerceForKind } from "./coerce";
import { discoverForms } from "./discovery";
import { formatForDisplay, markField, unmarkField, valuesEqual, withApplying } from "./markers";
import { valueFitsWhole } from "./snapshot";
import type { AppliedChange, ApplyResult, DiscoveredField, DiscoveredForm, FormUpdate, RejectedUpdate, RejectionReason, RevertResult } from "./types";

type Lookup = { form: DiscoveredForm; field: DiscoveredField } | { reason: RejectionReason };

function lookup(forms: DiscoveredForm[], formId: string, path: string): Lookup {
  const form = forms.find((candidate) => candidate.formId === formId);
  if (!form) {
    return { reason: "stale" };
  }
  const field = form.fields.find((candidate) => candidate.path === path);
  if (!field) {
    return { reason: "unknown-path" };
  }
  return { form, field };
}

function settableRejection(field: DiscoveredField): RejectionReason | undefined {
  if (field.descriptor.excluded) {
    return field.descriptor.reason === "unsupported" ? "unsupported" : "excluded";
  }
  if (field.descriptor.readOnly) {
    return "read-only";
  }
  // Judged on the live value, not on the snapshot the server checked: a value that has since
  // grown past what a snapshot carries whole was never shown to the model in full.
  if (!valueFitsWhole(field.adapter.currentValue())) {
    return "truncated";
  }
  return undefined;
}

function applyOne(forms: DiscoveredForm[], update: FormUpdate): AppliedChange | RejectedUpdate {
  const found = lookup(forms, update.formId, update.path);
  if ("reason" in found) {
    return { update, reason: found.reason };
  }
  const { field } = found;
  const { label } = field.descriptor;
  const rejection = settableRejection(field);
  if (rejection) {
    return { update, label, reason: rejection };
  }
  const options = field.adapter.options();
  const coerced = coerceForKind(update.value, { kind: field.adapter.kind, format: field.descriptor.format, options });
  if (!coerced.ok) {
    return { update, label, reason: coerced.reason, detail: coerced.detail };
  }
  const previousValue = field.adapter.currentValue();
  // Flushed through React before the next update: a kit input writes into the form's current
  // model object, and two writes in one batch would both start from the same stale model.
  const outcome = withApplying(() => flushSync(() => field.adapter.apply(coerced.value)));
  if (!outcome.applied) {
    return { update, label, reason: outcome.reason, detail: outcome.detail, validator: outcome.validator };
  }
  if (field.adapter.element) {
    markField(field.adapter.element, previousValue, options);
  }
  return {
    formId: update.formId,
    path: update.path,
    previousValue,
    appliedValue: coerced.value,
    label,
    displayValue: formatForDisplay(coerced.value, options),
  };
}

/**
 * Applies the assistant's updates against the page as it is now — the snapshot the server
 * validated against may be stale, so every update is checked again: the form must still be
 * there, the field listed, settable, and its value coercible and valid for the input.
 */
export function applyUpdates(updates: FormUpdate[], root: ParentNode = document): ApplyResult {
  const forms = discoverForms(root);
  const result: ApplyResult = { applied: [], rejected: [] };
  updates.forEach((update) => {
    const outcome = applyOne(forms, update);
    if ("reason" in outcome) {
      result.rejected.push(outcome);
    } else {
      result.applied.push(outcome);
    }
  });
  return result;
}

/**
 * Puts the previous values back, latest change first so a field set twice ends where it began.
 * Fields are found again by form and path — element references go stale when React re-renders.
 * A field the admin has edited since keeps their value: revert undoes the assistant, never them.
 */
export function revertChanges(changes: AppliedChange[], root: ParentNode = document): RevertResult {
  const forms = discoverForms(root);
  const result: RevertResult = { reverted: [], missing: [], hidden: [], superseded: [] };
  changes
    .slice()
    .reverse()
    .forEach((change) => {
      const found = lookup(forms, change.formId, change.path);
      if ("reason" in found) {
        (found.reason === "stale" ? result.missing : result.hidden).push(change);
        return;
      }
      if (!valuesEqual(found.field.adapter.currentValue(), change.appliedValue)) {
        result.superseded.push(change);
        return;
      }
      withApplying(() => flushSync(() => found.field.adapter.restore(change.previousValue)));
      if (found.field.adapter.element) {
        unmarkField(found.field.adapter.element);
      }
      result.reverted.push(change);
    });
  return result;
}
